import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import { Modifier } from 'react-day-picker/types/Modifiers';
import moment from 'moment';

interface DayPickerInputProps {
  handleFormChange?: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => void;
  handleStateChange?: (value: any) => void;
  value?: number;
  id: string;
  disabled: boolean;
  handleBlur?: (e: React.FocusEvent<any>) => void;
  onChange?: (e: React.ChangeEvent<any>) => void;
  disabledDays?: Modifier | Modifier[];
  onValidationError?: (error: string | null) => void;
}

export default function DayPickerInputValue({
  handleFormChange,
  handleStateChange,
  onChange,
  handleBlur,
  id,
  value,
  disabledDays,
  onValidationError,
}: DayPickerInputProps): JSX.Element {
  const [validationError, setValidationError] = useState<string | null>(null);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      if (!disabledDays) return false;

      const modifiers = Array.isArray(disabledDays)
        ? disabledDays
        : [disabledDays];
      return modifiers.some((mod) => matchModifier(date, mod));
    },
    [disabledDays],
  );

  const getErrorMessage = useCallback((date: Date): string => {
    const dayOfWeek = date.getDay();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return 'Weekends are not available. Please select a weekday.';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return 'Past dates are not available. Please select a future date.';
    }

    return 'This date is not available. Please select another date.';
  }, []);

  const handleDayChange = useCallback(
    (day: Date | undefined) => {
      setValidationError(null);
      onValidationError?.(null);

      if (!day) {
        handleFormChange?.(id, day, true) || handleStateChange?.(day);
        return;
      }

      if (isDateDisabled(day)) {
        const errorMsg = getErrorMessage(day);
        setValidationError(errorMsg);
        onValidationError?.(errorMsg);

        console.warn(
          `Invalid date selected: ${day.toDateString()} - ${errorMsg}`,
        );
        return;
      }

      handleFormChange?.(id, day, true) || handleStateChange?.(day);
    },
    [
      id,
      isDateDisabled,
      getErrorMessage,
      handleFormChange,
      handleStateChange,
      onValidationError,
    ],
  );

  // Handle manual input changes (when user types directly)
  const handleInputChange = useCallback(
    (e: any) => {
      // Call the original onChange if provided
      onChange?.(e);

      // Clear validation error while typing
      if (validationError) {
        setValidationError(null);
        onValidationError?.(null);
      }
    },
    [onChange, validationError, onValidationError],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialMonth = useMemo(() => {
    if (!disabledDays) return new Date();

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = today.getDate(); day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const disabled = Array.isArray(disabledDays)
        ? disabledDays.some((mod) => matchModifier(date, mod))
        : matchModifier(date, disabledDays);

      if (!disabled) return today;
    }

    return new Date(year, month + 1, 1);
  }, [disabledDays]);

  return (
    <div className="date-picker-wrapper">
      <DayPickerInput
        placeholder="DD/MM/YYYY"
        format="DD/MM/YYYY"
        formatDate={MomentLocaleUtils.formatDate}
        parseDate={MomentLocaleUtils.parseDate}
        onDayChange={handleDayChange}
        onChange={handleInputChange}
        onBlur={handleBlur}
        value={value ? new Date(value) : ''}
        inputProps={{
          className: validationError ? 'date-input-error' : undefined,
        }}
        dayPickerProps={{
          disabledDays: disabledDays || [],
          fromMonth: new Date(),
          initialMonth,
        }}
      />
      {validationError && (
        <div
          className="date-validation-error"
          style={{
            color: '#ff5757',
            fontSize: '0.875rem',
            marginTop: '4px',
            fontWeight: 500,
          }}
        >
          {validationError}
        </div>
      )}
    </div>
  );
}

const matchModifier = (date: Date, mod: Modifier): boolean => {
  if (!mod) return false;
  if (mod instanceof Date) return moment(mod).isSame(date, 'day');
  if (typeof mod === 'function') return mod(date);
  if (typeof mod === 'object') {
    const modObj = mod as any;
    const { before, after, from, to, daysOfWeek } = modObj;

    // Handle weekend checking
    if (daysOfWeek && Array.isArray(daysOfWeek)) {
      const dayOfWeek = date.getDay();
      if (daysOfWeek.includes(dayOfWeek)) return true;
    }

    return (
      (before && date < before) ||
      (after && date > after) ||
      (from && to && date >= from && date <= to) ||
      false
    );
  }
  return false;
};
